import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { isNil } from 'lodash-es';
import { Button, modalConfirm } from 'components/antd';
import { VehicleIcon } from 'AppClient/components/VehicleIcon';
import { Fragment, CCDisplay, ReferenceFieldsDisplay, PersonalDetailsDisplay } from 'components';
import * as converters from 'utils/converters';
import {
	AddressesStore,
	BookingDictionariesStore,
	NewBookingsStore,
	PassengerDetailsStore,
	RootStore,
	UpcomingBookingsStore,
	BookingCCStore,
} from 'AppClient/stores';
import classNames from 'classnames';
import OutsideClick from 'react-outside-click-handler';

import styles from './styles.module.scss';
import { Moment } from 'moment';
import { AddressesView } from './AddressesView';
import Icon from 'components/antd/Icon/Icon';
import { ThemeContext } from 'app-themes';
import { GoogleAddressAndFlightData, CreditCard, BookingCreditCard } from 'app-types';
import { StaticMap } from './StaticMap';
import AlternativeContactsDisplay from 'components/AlternativeContacts/AlternativeContactsDisplay';
import ChargeableText from './ChargeableText';

interface Props {
	newBookingsStore?: NewBookingsStore;
	bookingDictionariesStore?: BookingDictionariesStore;
	addressesStore?: AddressesStore;
	upcomingBookingsStore?: UpcomingBookingsStore;
	passengerDetailsStore?: PassengerDetailsStore;
	bookingCCStore?: BookingCCStore;
	tripDate?: string | Moment;
	bookingNumber?: string;
	globalTransfer?: {
		tripDistanceInMeters?: number;
		tripDurationInSeconds?: number;
	};
	bookingId?: number;
	addresses: Array<GoogleAddressAndFlightData | undefined>;
	isNewTrip: boolean;
	carTypeId?: number | string | null;
	numberOfPassengers: number;
	servicePartnerName?: string;
	priceDetails?: {
		currencySymbol: string;
		price: number;
	};
	index?: number;
	addressesObj?: {
		[label: string]: GoogleAddressAndFlightData | string | undefined;
	};
}
interface State {
	fixedHeight?: number;
	isDetailsVisible: boolean;
}
@inject(
	({
		newBookingsStore,
		bookingDictionariesStore,
		addressesStore,
		upcomingBookingsStore,
		passengerDetailsStore,
		bookingCCStore,
	}: RootStore) => ({
		newBookingsStore,
		bookingDictionariesStore,
		addressesStore,
		upcomingBookingsStore,
		passengerDetailsStore,
		bookingCCStore,
	})
)
@observer
export class TripDetails extends Component<Props, State> {
	state: State = {
		isDetailsVisible: false,
		fixedHeight: undefined,
	};

	static defaultProps = {
		isNewTrip: true,
	};

	private onEditHandler = () => {
		this.props.newBookingsStore!.editUpcomingTrip(this.props.bookingId!);
	};
	static contextType = ThemeContext;

	componentDidMount() {
		if (this.props.index) {
			// for upcoming bookings to keep markup in case of expandend view
			const el = document.getElementById(`upcomingBooking-${this.props.index}`);
			this.setState({
				fixedHeight: el ? el.clientHeight : undefined,
			});
		}
	}

	toggleDetails = () => {
		this.setState({
			isDetailsVisible: !this.state.isDetailsVisible,
		});
	};

	showModalConfirmModalCallback = (creditCard?: CreditCard | BookingCreditCard) => {
		const { bookingId } = this.props;
		const { cancelUpcomingTrip, cancellationFee } = this.props.upcomingBookingsStore!;
		const {
			chargedPercentages,
			chargedFeeAmountInCustomerCurrency,
			isLateCancellationAllowed,
		} = cancellationFee;
		const theme = this.context;

		const hasCancellationFee =
			!isNil(chargedPercentages) || !isNil(chargedFeeAmountInCustomerCurrency);

		modalConfirm({
			title: 'Cancel upcoming trip?',
			okText: 'Cancel trip',
			cancelText: 'Back',
			okButtonProps: {
				disabled: isLateCancellationAllowed === false,
			},
			content: hasCancellationFee ? (
				<ChargeableText cancellationFee={cancellationFee} creditCard={creditCard} />
			) : null,
			theme,
			onOk() {
				if (bookingId) {
					cancelUpcomingTrip(bookingId);
				}
			},
		});
	};

	getCancellationFeeAndShowModal = () => {
		this.props.upcomingBookingsStore!.getCancellationFee(
			this.props.bookingId!,
			this.showModalConfirmModalCallback
		);
	};

	expandIcon = () => {
		const { backToEdit } = this.props.newBookingsStore!;
		const { isNewTrip, priceDetails } = this.props;
		return (
			<Fragment>
				{isNewTrip ? (
					<Button size="small" icon="edit" onClick={backToEdit}>
						Edit
					</Button>
				) : (
					<span className={styles.tripDetailsCost}>
						<span>{priceDetails!.currencySymbol}</span>
						&nbsp;
						<span>{converters.roundNumber(priceDetails!.price)}</span>
					</span>
				)}
			</Fragment>
		);
	};

	getGlobalTransfer = () => {
		const { globalTransfer, isNewTrip } = this.props;
		const { tripDistanceInMeters, tripDurationInSeconds } = globalTransfer!;
		if (tripDistanceInMeters && tripDurationInSeconds) {
			const miles = converters.metersToMiles(tripDistanceInMeters);
			const time = converters.secondsToMinutes(tripDurationInSeconds);
			return `Est. ${miles} mi, ${time.value} ${time.preferSuffix} ${
				isNewTrip ? 'ride' : ''
			}`;
		}
		return null;
	};

	onClickOutsideHandler = () => {
		if (this.state.isDetailsVisible) {
			this.setState({ isDetailsVisible: false });
		}
	};

	render() {
		const { isDetailsVisible, fixedHeight } = this.state;
		const {
			tripDate = '',
			isNewTrip,
			carTypeId,
			bookingNumber,
			servicePartnerName = '',
			numberOfPassengers,
			index,
			addressesObj,
		} = this.props;
		const { findCarById, countriesDictionary } = this.props.bookingDictionariesStore!;
		const { airportTypes } = this.props.addressesStore!;
		const { currentBookingCard } = this.props.bookingCCStore!;
		const { userInfo } = this.props.passengerDetailsStore!;

		const carDetails = findCarById(carTypeId);
		const theme = this.context;

		return (
			<div
				className={classNames({ [styles.upcomingBookingItem]: !isNewTrip })}
				style={{ height: fixedHeight }}
			>
				<OutsideClick onOutsideClick={this.onClickOutsideHandler}>
					<div
						id={`upcomingBooking-${index}`}
						className={classNames({
							[styles.gettingPrice]: isNewTrip,
							[styles.upcomingBooking]: !isNewTrip,
							[styles.upcomingBookingOpen]: isDetailsVisible,
							[styles[`upcomingBooking-${theme}`]]: theme !== '',
						})}
					>
						{isNewTrip ? <h2>trip details</h2> : null}

						<div
							className={classNames({
								'modalBox modalBoxBooking': isNewTrip,
							})}
						>
							{isNewTrip && <StaticMap />}
							<div
								className={classNames({
									modalBoxRow: isNewTrip,
								})}
							>
								<div
									className={classNames({
										modalBoxCol: isNewTrip,
									})}
								>
									<div className={styles.tripDetails}>
										<div
											className={classNames(
												styles.tripDetailsBox,
												styles.dateTime
											)}
										>
											<Icon type="dateTimePicker" />
											<span className={styles.tripDetailsUpComing}>
												{converters.datetimeWithTimeZone(tripDate)}
											</span>
											{bookingNumber && (
												<span className={styles.tripDetailsBookingNumber}>
													# {bookingNumber}
												</span>
											)}
										</div>

										<div
											className={classNames(
												styles.tripDetailsBox,
												styles.rout
											)}
										>
											<AddressesView
												addresses={this.props.addresses}
												addressesObj={addressesObj}
												showShortAddress={!this.state.isDetailsVisible}
												airportTypes={isNewTrip ? airportTypes : undefined}
											/>
										</div>

										<div
											className={classNames(
												styles.tripDetailsBox,
												styles.details
											)}
										>
											<Button
												size="small"
												icon="right"
												className={classNames(styles.btnToggle, {
													open: this.state.isDetailsVisible,
												})}
												onClick={this.toggleDetails}
											>
												Details
											</Button>
											{this.expandIcon()}
										</div>

										{isDetailsVisible && (
											<div className={styles.tripDetailsAdditional}>
												{isNewTrip && currentBookingCard && (
													<div className={styles.tripDetailsBox}>
														<CCDisplay
															creditCard={currentBookingCard}
														/>
													</div>
												)}
												{isNewTrip ? null : (
													<div
														className={classNames(
															styles.tripDetailsBox,
															styles.transferInfo
														)}
													>
														<span
															className={
																styles.tripDetailsPartnerName
															}
														>
															{servicePartnerName}
														</span>
														<span
															className={styles.tripDetailsDuration}
														>
															{this.getGlobalTransfer()}
														</span>
													</div>
												)}

												<div
													className={classNames(
														styles.tripDetailsBox,
														styles.carType
													)}
												>
													{carDetails && (
														<Fragment>
															<VehicleIcon
																carTypeName={carDetails.carTypeName}
															/>
															<span>
																{`${carDetails.carTypeName}, ${numberOfPassengers} pass`}
															</span>
														</Fragment>
													)}
												</div>
												{isNewTrip && (
													<div>
														<PersonalDetailsDisplay
															title={'Personal details'}
															personalDetails={userInfo!}
															className={
																styles.alternativeContactsRow
															}
															allCountries={countriesDictionary}
															itemGridWidth={4}
														/>
														<AlternativeContactsDisplay
															title={'Alternative contacts'}
															alternativeContactList={
																userInfo!.alternativeContactList
															}
															className={
																styles.alternativeContactsRow
															}
															allCountries={countriesDictionary}
															itemGridWidth={4}
														/>
														<div className={styles.tripReference}>
															<ReferenceFieldsDisplay
																title={'Reference fields'}
																numOfItemsInRow={3}
																referenceFields={
																	userInfo!.referenceFields
																}
															/>
														</div>
													</div>
												)}

												{isNewTrip ? null : (
													<div
														className={classNames(
															styles.tripDetailsBox,
															styles.controls
														)}
													>
														<Button
															onClick={
																this.getCancellationFeeAndShowModal
															}
														>
															Cancel trip
														</Button>
														<Button
															type="primary"
															onClick={this.onEditHandler}
														>
															Edit trip
														</Button>
													</div>
												)}
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</OutsideClick>
			</div>
		);
	}
}
